import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { IOvertimeUpdateParam } from '../../../../models/overtime/OvertimeModel';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import Popup from '../../../../components/common-components/pop-up/Popup';
import styles from './NewOvertimePopup.module.css';

const EditOvertimePopup = () => {
  const { popupStore, overtimeStore, authStore } = useStore();
  const [formData, setFormData] = useState<IOvertimeUpdateParam>({
    startDate: undefined,
    endDate: undefined,
    description: '',
    status: 'Pending',
    overtimeType: 'Overtime',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (overtimeStore.selectedOvertime) {
      setFormData({
        startDate: new Date(overtimeStore.selectedOvertime.startDate),
        endDate: new Date(overtimeStore.selectedOvertime.endDate),
        description: overtimeStore.selectedOvertime.description,
        status: overtimeStore.selectedOvertime.status,
        overtimeType: overtimeStore.selectedOvertime.overtimeType,
      });
    }
  }, [overtimeStore.selectedOvertime]);

  const calculateTotalHours = (start: Date, end: Date): number => {
    const diffInMs = end.getTime() - start.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.round(diffInHours * 100) / 100; // 2 decimal places
  };

  const formatDateTimeForInput = (date: Date): string => {
    // Geçersiz tarih kontrolü
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    // Basit format - timezone sorununu önlemek için
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeFromInput = (inputValue: string): Date => {
    if (!inputValue) {
      return new Date();
    }

    // Basit parse - timezone sorununu önlemek için
    const date = new Date(inputValue);

    // Geçersiz tarih kontrolü
    if (isNaN(date.getTime())) {
      return new Date();
    }

    return date;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = 'Başlangıç tarihi gereklidir';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi gereklidir';
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
    }

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Açıklama gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !overtimeStore.selectedOvertime) {
      return;
    }

    setIsSubmitting(true);
    try {
      await overtimeStore.updateOvertime(overtimeStore.selectedOvertime.id, formData);
      popupStore.setIsEditOvertimeOpen(false);
    } catch (error) {
      console.error('Error updating overtime:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsEditOvertimeOpen(false);
    overtimeStore.setSelectedOvertime(null);
    setErrors({});
  };

  const handleInputChange = (field: keyof IOvertimeUpdateParam, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Hata varsa temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleFormSubmit = () => {
    if (!validateForm() || !overtimeStore.selectedOvertime) {
      return;
    }

    setIsSubmitting(true);
    (async () => {
      try {
        await overtimeStore.updateOvertime(overtimeStore.selectedOvertime!.id, formData);
        popupStore.setIsEditOvertimeOpen(false);
      } catch (error) {
        console.error('Error updating overtime:', error);
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  if (!overtimeStore.selectedOvertime) {
    return null;
  }

  return (
    <Popup
      isOpen={popupStore.isEditOvertimeOpen}
      onClose={handleClose}
      title="Mesai Kaydını Düzenle"
      footer={
        <>
          <CommonButton type="cancel" onClick={handleClose} disabled={isSubmitting}>
            İptal
          </CommonButton>
          <CommonButton type="save" onClick={handleFormSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Güncelleniyor...' : 'Güncelle'}
          </CommonButton>
        </>
      }
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          handleFormSubmit();
        }}
      >
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Başlangıç Tarihi</label>
            <input
              type="datetime-local"
              className={styles.formInput}
              value={formData.startDate ? formatDateTimeForInput(formData.startDate) : ''}
              onChange={e => handleInputChange('startDate', parseDateTimeFromInput(e.target.value))}
            />
            {errors.startDate && <div className={styles.errorMessage}>{errors.startDate}</div>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Bitiş Tarihi</label>
            <input
              type="datetime-local"
              className={styles.formInput}
              value={formData.endDate ? formatDateTimeForInput(formData.endDate) : ''}
              onChange={e => handleInputChange('endDate', parseDateTimeFromInput(e.target.value))}
            />
            {errors.endDate && <div className={styles.errorMessage}>{errors.endDate}</div>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Toplam Saat</label>
          <input
            type="text"
            className={styles.formInput}
            value={
              formData.startDate && formData.endDate
                ? calculateTotalHours(formData.startDate, formData.endDate) + ' saat'
                : '0 saat'
            }
            readOnly
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Açıklama</label>
          <textarea
            className={styles.formTextarea}
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Mesai açıklamasını giriniz..."
          />
          {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
        </div>

        {authStore.hasAnyRole(['Admin']) && (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Durum</label>
            <select
              className={styles.formSelect}
              value={formData.status}
              onChange={e => handleInputChange('status', e.target.value)}
            >
              <option value="Pending">Beklemede</option>
              <option value="Approved">Onaylandı</option>
              <option value="Rejected">Reddedildi</option>
            </select>
          </div>
        )}

        {formData.status === 'Rejected' && (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Red Sebebi</label>
            <textarea
              className={styles.formTextarea}
              value={formData.rejectionReason || ''}
              onChange={e => handleInputChange('rejectionReason', e.target.value)}
              placeholder="Red sebebini giriniz..."
            />
          </div>
        )}
      </form>
    </Popup>
  );
};

export default observer(EditOvertimePopup);
